import React from "react";
import { useQuery } from "@apollo/client";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { engagementReport, engagementSummary, engagementReportByRange, engagementSummaryByRange } from "./graphql";
import { AnalyticsReportQuery } from "./analyticsTypes";
import {
  compareSortValues,
  formatDurationSeconds,
  normalizeDimensionValue,
  parseMetricValue,
} from "./analyticsUtils";

type ChartRow = Record<string, string | number>;

type EnrichedRow = {
  normalizedDimensionValue: string;
  sortValue: number | string;
  metrics: Record<string, number>;
};

const findHeaderIndex = (headers: string[], target: string) =>
  headers.findIndex((header) => header.toLowerCase() === target.toLowerCase());

interface EngagementCardProps {
  startDate?: string;
  endDate?: string;
}

export const EngagementCard = ({ startDate, endDate }: EngagementCardProps = {}) => {
  const hasRange = !!startDate && !!endDate;
  const rangeVars = hasRange ? { variables: { startDate, endDate } } : undefined;
  const { data, loading, error } = useQuery<AnalyticsReportQuery>(
    hasRange ? engagementReportByRange : engagementReport,
    rangeVars
  );
  const { data: summaryData } = useQuery<AnalyticsReportQuery>(
    hasRange ? engagementSummaryByRange : engagementSummary,
    rangeVars
  );

  const report = data?.analyticsReport ?? null;
  const summaryReport = summaryData?.analyticsReport ?? null;
  const dimensionKey = report?.dimensionHeaders?.[0] ?? "date";

  const chartData = React.useMemo<ChartRow[]>(() => {
    if (!report) return [];
    const engagementIndex = findHeaderIndex(
      report.metricHeaders,
      "userEngagementDuration"
    );
    const activeUsersIndex = findHeaderIndex(report.metricHeaders, "activeUsers");

    const enrichedRows = report.rows.map((row) => {
      const rawDimensionValue = row.dimensionValues[0] ?? "";
      const normalizedDimensionValue = normalizeDimensionValue(rawDimensionValue);
      const sortValue = /^\d{8}$/.test(rawDimensionValue)
        ? Number(rawDimensionValue)
        : normalizedDimensionValue;
      const engagementSeconds =
        engagementIndex >= 0
          ? parseMetricValue(row.metricValues[engagementIndex])
          : 0;
      const activeUsers =
        activeUsersIndex >= 0
          ? parseMetricValue(row.metricValues[activeUsersIndex])
          : 0;
      const avgEngagementPerUser =
        activeUsers > 0 ? engagementSeconds / activeUsers : 0;

      return {
        normalizedDimensionValue,
        sortValue,
        metrics: { avgEngagementPerUser },
      } as EnrichedRow;
    });

    return enrichedRows
      .sort((a, b) => compareSortValues(a.sortValue, b.sortValue))
      .map((item) => ({
        [dimensionKey]: item.normalizedDimensionValue,
        ...item.metrics,
      }));
  }, [report, dimensionKey]);

  const summaryValues = React.useMemo(() => {
    if (!summaryReport || summaryReport.rows.length === 0) {
      return { avgTime: 0, engagedSessionsPerUser: 0 };
    }

    const engagementIndex = findHeaderIndex(
      summaryReport.metricHeaders,
      "userEngagementDuration"
    );
    const activeUsersIndex = findHeaderIndex(
      summaryReport.metricHeaders,
      "activeUsers"
    );
    const engagedSessionsIndex = findHeaderIndex(
      summaryReport.metricHeaders,
      "engagedSessions"
    );
    const summaryRow = summaryReport.rows[0];

    const engagementSeconds =
      engagementIndex >= 0
        ? parseMetricValue(summaryRow.metricValues[engagementIndex])
        : 0;
    const activeUsers =
      activeUsersIndex >= 0
        ? parseMetricValue(summaryRow.metricValues[activeUsersIndex])
        : 0;
    const engagedSessions =
      engagedSessionsIndex >= 0
        ? parseMetricValue(summaryRow.metricValues[engagedSessionsIndex])
        : 0;

    return {
      avgTime: activeUsers > 0 ? engagementSeconds / activeUsers : 0,
      engagedSessionsPerUser:
        activeUsers > 0 ? engagedSessions / activeUsers : 0,
    };
  }, [summaryReport]);

  if (loading) {
    return (
      <Box sx={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        Failed to load chart.
      </Typography>
    );
  }

  if (!report) {
    return (
      <Typography variant="body2" color="text.secondary">
        No data available.
      </Typography>
    );
  }

  const avgMetricKey = "avgEngagementPerUser";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", gap: 3 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Avg engagement time
          </Typography>
          <Typography variant="h6">
            {formatDurationSeconds(summaryValues.avgTime)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Engaged sessions / user
          </Typography>
          <Typography variant="h6">
            {summaryValues.engagedSessionsPerUser.toFixed(1)}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ width: "100%", height: 160 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData.slice(-30)} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={dimensionKey} hide />
            <YAxis
              width={40}
              tickFormatter={(value) => formatDurationSeconds(Number(value))}
            />
            <Tooltip
              formatter={(value: number) => formatDurationSeconds(Number(value))}
            />
            <Line
              type="monotone"
              dataKey={avgMetricKey}
              stroke="#1e88e5"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};
